import { SocialShare } from "@quintype/components";
import get from "lodash/get";
import throttle from "lodash/throttle";
import { array, bool, func, number, object, string } from "prop-types";
import React, { useEffect, useRef, useState, Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";

import { HeroImage, SocialShareTemplate } from "../../../arrow";
import { CaptionAttribution } from "../../../arrow/components/Atoms/CaptionAttribution";
import { STATIC_KEYWORDS } from "../../../constants";
import { DfpComponent } from "../../ads/dfp-component";
import {
  BreadCrumb,
  Button,
  Headline,
  PublishDateAndTime,
  ReadTime,
  StoryAuthors,
  StoryAuthorImages,
} from "../../atoms";
import FortuneExchange from "../../atoms/FortuneExchange";
import LiveButton from "../../atoms/live-button";
import { useAiFaqData } from "../../customHooks/useAiFaqData";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { useSummaryCheck } from "../../customHooks/useSummaryCheck";
import { STORY_HEADER_IN_VIEW } from "../../store/actions";
import { MagazineStoryHeaderCard } from "../MagazineStoryHeaderCard";
import { TextWithReadMore } from "../../molecules/TextWithReadMore";
import InThisStoryWidget from "../InThisStoryWidget";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import { isMobile } from "react-device-detect";
import { getCreatorSocialTitle } from "../../utils";
import "./StoryHeader.m.css";

const SummaryCard = lazy(() => import("../SummaryCard"));

const StoryHeader = ({
  showAd,
  story,
  headline,
  storyAuthorData,
  publishTime,
  readTime,
  showHeroImage,
  setShowHighlights,
  magazineDetails,
}) => {
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const fullWidth = !showAd ? "full-width" : "";
  const storyHeaderRef = useRef(null);
  const qtStates = useSelector((state) => state);
  const member = useSelector((state) => get(state, ["member"], null));
  const qtState = get(qtStates, ["qt"], {});
  const qtConfig = get(qtState, ["config"], {});
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  const storyHeaderInView = get(qtStates, "storyHeaderInView", true);
  const storyTemplate = get(story, ["story-template"], "");
  const isLiveBlog = storyTemplate === "live-blog";
  const isPhotoStory = storyTemplate === "photo";
  const isSponsoredStory = storyTemplate === "sponsored";
  const isCreatorStory = storyTemplate === "creator";
  const isSummaryEligibleStory = ["text", "magazine"].includes(storyTemplate);
  const storyMetadata = get(story, ["metadata"], "");
  const isWebView = get(qtState, ["config", "isWebView"]) === true;
  const faqUrl = get(qtConfig, ["publisher-attributes", "publisher-faq-url"], false);
  const aiSummaryPublicIdentifierCutoff = parseInt(
    get(qtConfig, ["publisher-attributes", "ai-summary-public-identifier-cutoff"], 0),
    10
  );
  const storyId = get(story, ["id"], "") || get(story, ["story-content-id"], "");
  const storyPublicIdentifier = parseInt(story?.["public-identifier"] || story?.["sequence-no"], 10);

  const creatorAttributes = {
    creator_headline: get(storyMetadata, ["creator-headline"], "").trim(),
    creator_designation: get(storyMetadata, ["creator-designation"], "").trim(),
  };

  const { summaryPresent, showSummary, setShowSummary, summaryIndex } = useSummaryCheck(story.cards);
  const { hasSummary, summary, headline: apiHeadline, heroImageS3Key: apiHeroImageS3Key, isLoading } = useAiFaqData({
    faqUrl,
    storyId,
    enabled: isSummaryEligibleStory && faqUrl !== false,
  });
  const hasApiSummary = hasSummary && Boolean(summary);
  const canUseApiSummaryFallback =
    !summaryPresent &&
    Number.isFinite(storyPublicIdentifier) &&
    Number.isFinite(aiSummaryPublicIdentifierCutoff) &&
    storyPublicIdentifier > aiSummaryPublicIdentifierCutoff;
  // On article pages, the FAQ API is only the fallback when no authored CMS summary element exists.
  const shouldUseApiSummary = canUseApiSummaryFallback && hasApiSummary;
  const shouldShowSummary = summaryPresent || shouldUseApiSummary;
  const shouldRenderSummaryButton =
    (((!isSponsoredStory && story["story-template"] === "text") || story["story-template"] === "magazine") &&
      !isWebView &&
      (summaryPresent || canUseApiSummaryFallback));
  const isSummaryButtonEnabled = shouldShowSummary;
  const isSummaryButtonPending = !summaryPresent && canUseApiSummaryFallback && isLoading;
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";
  const [btnScrolled, setBtnScrolled] = useState(false);
  const [appClick, setAppClick] = useState(false);
  const inThisStoryWidgetPresent = qtState?.data?.story?.tags?.some((item) => {
    const props = item.properties || {};
    return (
      props.next500ranks ||
      props.fortune500ranks ||
      props.emergingcompaniesranks ||
      props["40u40ranks"] ||
      props.mpwranks
    );
  });
  const domainName = get(qtConfig, ["publisher-attributes", "publisher-host"], "");

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setBtnScrolled(true);
      } else {
        setBtnScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScroll = () => {
    const storyHeaderPosition = storyHeaderRef.current.getBoundingClientRect();
    if (storyHeaderPosition.bottom < 75 && storyHeaderInView) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: false,
      });
    } else if (storyHeaderPosition.bottom > 75 && !storyHeaderInView) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: true,
      });
    }
  };

  const trackStoryInteraction = (eventAction, callback) => {
    const pageType = "story-page";
    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, qtConfig, story);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_story_interactions",
      event_action: eventAction,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
    if (typeof callback === "function") {
      callback();
    }
  };

  const handleHighlightsClick = () => {
    trackStoryInteraction("highlight_clicks", () => setShowHighlights(true));
  };

  const handleSummaryClick = () => {
    if (!isSummaryButtonEnabled) {
      return;
    }
    trackStoryInteraction("summary_click", () => setShowSummary(true));
  };

  const throttledHandleScroll = throttle(handleScroll, 200);
  useEffect(() => {
    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);

  useEffect(() => {
    if (showSummary) {
      document.body.classList.add("no-scroll-forSummary");
      document.body.classList.remove("scroll-forSummar");
    } else {
      document.body.classList.remove("no-scroll-forSummary");
      document.body.classList.add("scroll-forSummar");
    }
    return () => {
      document.body.classList.remove("scroll-forSummar");
      document.body.classList.remove("no-scroll-forSummary");
    };
  }, [showSummary]);

  const iconSize = { width: "18px", height: "18px" };
  const shareTitle = getCreatorSocialTitle(story) || story.headline;

  if (!headline) return null;
  const isMagazineType = story["story-template"] === "magazine";

  const trackPopupSocialShareClick = (platform) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = "story-page";
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        story
      );

      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "share_click",
        link_text: platform || "share",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const SocialShareComponent = () => {
    return (
      <div id={`story-share-${story.id}`} className="content-style">
        <SocialShare
          template={(props) => (
            <SocialShareTemplate
              {...props}
              story={story}
              shareTitle={shareTitle}
              qtConfig={qtState.config}
              onShareClick={(platform) => trackPopupSocialShareClick(platform?.toLowerCase())}
            />
          )}
          fullUrl={encodeURI(story.url)}
          title={shareTitle}
          shareIconId={`share-icon-story-share-${story.id}`}
          buttonLabel={"Share"}
        />
      </div>
    );
  };

  const isMagazineStoryWrapper = isMagazineType && magazineDetails;
  let adConfig = get(qtState, ["config", "ads-config", "ad_slots", qtState.pageType, "right_side"], {});
  if (qtState.pageType === "story-page" && qtState?.data?.story?.["story-template"] !== "text") {
    adConfig = get(
      qtState,
      ["config", "ads-config", "ad_slots", qtState?.data?.story?.["story-template"], "right_side"],
      {}
    );
  }
  const isAuthorImageRequired = get(story, ["metadata", "story-attributes", "authorimagerequired", "0"], false);

  const handleAppsClick = (e) => {
    setAppClick(true);
    const pageType = "story-page";
    if (typeof window !== "undefined") {
      const categoryInfo = getCategoryAndSubCategory(window.location, pageType, qtConfig, story);
      const templateName = getTemplateName(pageType === "story-page" ? story?.["story-template"] : pageType);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_app_install_click",
        link_url: `${domainName}/apps`,
        link_text: "App Download Floating Button",
        link_category: "NA",
        category: categoryInfo.category,
        sub_category: categoryInfo.subCategory,
        content_id: categoryInfo.contentId,
        template_name: templateName,
        platform: "mobile",
      });
    }
  };

  const creatorStoryContent = (story) => {
    const monthName = get(story, ["metadata", "story-attributes", "creatorofthemonth", "0"], "");
    const storyHeadline = get(story, ["headline"], "");
    const storyImage = get(story, ["hero-image-s3-key"], "");
    return (
      <>
        <div className="container" styleName="main-container" ref={storyHeaderRef}>
          {/* <div styleName="headline">Creator of the Month - {monthName}</div> */}
          {/* <div styleName="monthname">{creatorAttributes?.creator_headline}</div> */}
          <div styleName="creator-body-container">
            <div styleName="creator-featured-image">
              <img
                src={`https://media.fortuneindia.com/${storyImage}?w=1140&q=80`}
                alt={storyHeadline}
                title={storyHeadline}
                loading="lazy"
                width="1140"
                height="641"
              />
              <div styleName="creator-featured-info">
                <div styleName="creator-featured-info-inner">
                  <h1 styleName="name">{storyHeadline}</h1>
                  <h2 styleName="designation">{creatorAttributes?.creator_designation}</h2>
                  <div styleName="headlinelabel">Creator of the Month - {monthName}</div>
                  {/* <div styleName="synopsis">{story.subheadline}</div> */}
                </div>
              </div>
            </div>
          </div>
          <div styleName="synopsis">
            <div styleName="synopsis-title">{creatorAttributes?.creator_headline}</div>
            <div styleName="creator-timestap">
             <div styleName="authorsbox"> By <StoryAuthors
                  storyAuthor={storyAuthorData}
                  customStyleName={"authors-name"}
                  story={story}
                  config={qtState.config}
                  pageType="story-page"
                />
                </div>
                <div styleName="date-and-time">
                  <PublishDateAndTime publishTime={publishTime} /></div>
                  </div>
            {story.subheadline}</div>
        </div>
      </>
    );
  };

  if (isCreatorStory) {
    return creatorStoryContent(story);
  }

  return (
    <React.Fragment>
      <div className="container story-header-wrapper" styleName={`story-header-wrapper `}>
        {/* {(story["story-template"] === "text" || story["story-template"] === "magazine") && !isWebView && (
          <div styleName={btnScrolled ? "stickyGooglebox open" : "stickyGooglebox"}>
            <a href="/apps" onClick={handleAppsClick}>
              Install App
            </a>
          </div>
        )} */}
        <div
          styleName={`top-content ${isAdfreePage ? "adfree-header-wrapper" : ""} ${isMagazineStoryWrapper ? "magazine-story-header-wrapper" : ""
            }`}
          ref={storyHeaderRef}
        >
          <div
            styleName={`left-section ${fullWidth} ${isMagazineType ? "magazine-template" : ""} ${isMagazineStoryWrapper ? "" : "no-magazine-issue"
              }`}
          >
            <LiveButton story={story} />
            {!isSponsoredStory ? (
              <div styleName="breadcrumb-story">
                <BreadCrumb type="story-page" story={story} />
              </div>
            ) : null}

            {isSponsoredStory ? (
              <div styleName="sponsored-logo">
                <FortuneExchange />
                <div styleName="sponsored-tag">{STATIC_KEYWORDS.SPONSORED}</div>
              </div>
            ) : null}
            <Headline
              type={1}
              customStyleName={`h1-style ${isMagazineType ? "magazine" : ""}`}
              customClassName="article-header-headline"
            >
              {headline}
            </Headline>
            <div styleName="content-wrapper">
              {isAuthorImageRequired &&
                isAuthorImageRequired !== "false" &&
                story["story-template"] !== "sponsored" && <StoryAuthorImages authors={storyAuthorData} />}
              <div styleName="content">
                <StoryAuthors
                  storyAuthor={storyAuthorData}
                  customStyleName={"authors-name"}
                  story={story}
                  config={qtState.config}
                  pageType="story-page"
                />
                <div styleName="date-and-time">
                  <PublishDateAndTime publishTime={publishTime} />
                  {readTime ? (
                    <>
                      <span>/</span>
                      <ReadTime time={readTime} />
                    </>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div styleName="btns-wrapper">
                {isLiveBlog && !isWebView ? (
                  <Button
                    iconType="highlights"
                    iconHeight={iconSize.height}
                    iconWidth={iconSize.width}
                    customStyleName="story-header-btn"
                    onClickFunction={handleHighlightsClick}
                  >
                    Highlights
                  </Button>
                ) : (
                  shouldRenderSummaryButton && (
                    <Button
                      iconType="summary"
                      iconHeight={iconSize.height}
                      iconWidth={iconSize.width}
                      customStyleName="story-header-btn"
                      onClickFunction={handleSummaryClick}
                      ariaLabel={isSummaryButtonPending ? "Summary is loading" : "Open Summary"}
                    >
                      Summarise
                    </Button>
                  )
                )}
                {!isWebView && <SocialShareComponent />}
              </div>
            </div>
          </div>
          {!isAdfreePage && showAd && !isMagazineType ? (
            <div styleName="right-section">
              <DfpComponent
                adStyleName="ad-style-300x250"
                id="story-page-right-mrec"
                path={`${networkId}/${adConfig}`}
                adSlotConfigName="mRec_ad"
                showText={true}
              />
            </div>
          ) : isMagazineStoryWrapper && !isWebView ? (
            <MagazineStoryHeaderCard story={story} magazineDetails={magazineDetails} />
          ) : null}
        </div>
        {story?.subheadline && (
          <TextWithReadMore
            textElement={{ text: story?.subheadline }}
            deviceType={deviceType}
            isMagazine={isMagazineType}
            storyHeader={true}
          />
        )}
        {!isWebView && <InThisStoryWidget data={qtState?.data?.story} />}
      </div>
      {showHeroImage && story["hero-image-s3-key"] ? (
        <div
          styleName={`hero-image-wrapper ${story?.subheadline !== null || inThisStoryWidgetPresent ? "" : "no-subHeadline-wrapper"
            }`}
        >
          {!isAdfreePage && isMagazineStoryWrapper && (
            <DfpComponent
              adStyleName="ad-slot-size-300x250"
              id="magazine-story-page-right-mrec"
              path={`${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_Mgzn_mREC`}
              adSlotConfigName="mRec_ad"
              showText={true}
            />
          )}
          <HeroImage story={story} aspectRatio={[[16, 9]]} isStoryPageImage={true} showIcon={false} />
        </div>
      ) : null}
      {showHeroImage ? <CaptionAttribution story={story} /> : null}
      {(story["story-template"] === "text" || story["story-template"] === "magazine") && (
        <>
          <div styleName="storymidgooglesourncebtn">
            <a href="https://www.google.com/preferences/source?q=fortuneindia.com" target="_blank">
              <img
                src="https://media.fortuneindia.com/fortune-india/2025-09-20/oy5wzt7g/animate-fortune-logo.gif"
                alt="Google"
                title="Google"
                width="44"
                height="38"
                loading="lazy"
              />{" "}
              <div styleName="story-google-text">
                Set <span>Fortune India</span> <i styleName="checkIcon">&#10003;</i>
                <strong>As Preferred on Google</strong>
              </div>
              <button aria-label="Set Fortune India as source" styleName="proceed-button">
                Proceed
              </button>
            </a>
          </div>
        </>
      )}
      {shouldShowSummary && showSummary && (
        <Suspense fallback={<div></div>}>
          <SummaryCard
            setShowSummary={setShowSummary}
            story={story}
            summaryIndex={summaryIndex}
            summaryHtml={shouldUseApiSummary ? summary : ""}
            summaryTitle={shouldUseApiSummary ? apiHeadline : ""}
            summaryImage={shouldUseApiSummary ? apiHeroImageS3Key : ""}
          />
        </Suspense>
      )}
      {showSummary && <div styleName="blur-bg" />}
    </React.Fragment>
  );
};

StoryHeader.defaultProps = {
  showAd: true,
  headline: "",
  storyAuthorData: [],
  publishTime: 0,
  readTime: 0,
  showHeroImage: true,
};

StoryHeader.propTypes = {
  showAd: bool,
  showHeroImage: bool,
  headline: string.isRequired,
  storyAuthorData: array.isRequired,
  publishTime: number,
  readTime: number,
  story: object,
  setShowHighlights: func,
};

export default StoryHeader;
